import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	type ArtemisWebhookDeliverySortInput,
	CreateWebhookDocument,
	type CreateWebhookInput,
	DeleteWebhookDocument,
	GetWebhookDeliveriesDocument,
	GetWebhookDocument,
	GetWebhooksDocument,
	RotateWebhookSecretDocument,
	UpdateWebhookDocument,
	type UpdateWebhookInput,
} from "@/common/artemis-sdk/graphql";
import type {
	ArtemisWebhook,
	ArtemisWebhookDelivery,
	ArtemisWebhookListItem,
} from "../model";
import { useGqlClient } from "../use-gql-client";
import { toOrder, usePagedList } from "./use-paged-list";

/** Webhooks of a workspace (at most 20, so one page). Workspace-admin only. */
export function useWebhooks(workspaceId: string) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["webhooks", workspaceId],
		queryFn: async () => {
			const data = await gqlFetch(GetWebhooksDocument, {
				workspaceId,
				first: 50,
				order: [{ createdAt: "DESC" }],
			});
			return (data.webhooks?.nodes ?? []) as ArtemisWebhookListItem[];
		},
		enabled: !!workspaceId,
	});
}

export function useWebhook(webhookId: string) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["webhook", webhookId],
		queryFn: async () => {
			const data = await gqlFetch(GetWebhookDocument, { webhookId });
			return data.webhook as ArtemisWebhook | null;
		},
		enabled: !!webhookId,
	});
}

/** Server-side paginated delivery history for a webhook, newest first. */
export function useWebhookDeliveries(webhookId: string) {
	const gqlFetch = useGqlClient();
	return usePagedList<ArtemisWebhookDelivery>({
		baseKey: ["webhook-deliveries", webhookId],
		enabled: !!webhookId,
		initialSort: [{ id: "receivedAt", desc: true }],
		fetchPage: async ({ sort, window }) => {
			const data = await gqlFetch(GetWebhookDeliveriesDocument, {
				webhookId,
				...window,
				order: toOrder<ArtemisWebhookDeliverySortInput>(sort),
			});
			const c = data.webhookDeliveries;
			return {
				nodes: (c?.nodes as ArtemisWebhookDelivery[]) ?? [],
				totalCount: c?.totalCount ?? 0,
				pageInfo: c?.pageInfo ?? { hasNextPage: false, hasPreviousPage: false },
			};
		},
	});
}

/** Creates a webhook. The result carries its secret — the only time it is ever returned. */
export function useCreateWebhook() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: CreateWebhookInput) =>
			gqlFetch(CreateWebhookDocument, { input }),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["webhooks", variables.workspaceId],
			});
		},
	});
}

export function useUpdateWebhook(webhookId: string) {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: UpdateWebhookInput) =>
			gqlFetch(UpdateWebhookDocument, { webhookId, input }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["webhook", webhookId] });
			queryClient.invalidateQueries({ queryKey: ["webhooks"] });
		},
	});
}

/** Issues a new secret; the old one stops working immediately. The result carries the new secret. */
export function useRotateWebhookSecret(webhookId: string) {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => gqlFetch(RotateWebhookSecretDocument, { webhookId }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["webhook", webhookId] });
		},
	});
}

export function useDeleteWebhook() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (webhookId: string) =>
			gqlFetch(DeleteWebhookDocument, { webhookId }),
		onSuccess: (_, webhookId) => {
			queryClient.invalidateQueries({ queryKey: ["webhook", webhookId] });
			queryClient.invalidateQueries({ queryKey: ["webhooks"] });
		},
	});
}

/** The public URL a caller posts to. Same host as the GraphQL API. */
export function webhookUrl(webhookId: string): string {
	return new URL(
		`/v1/webhooks/${webhookId}`,
		import.meta.env.VITE_PLATFORM_API_URL,
	).toString();
}
